import {
  Button,
  CharCounter,
  Input,
  Select,
  Textarea,
  Tooltip,
} from "@/components";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  showToastError,
  showToastSuccess,
  showToastWarning,
} from "@/shared/alertService";
import apiService from "@/shared/apiService";
import { cn } from "@/utils/cn";
import { getPlatformName } from "@/utils/platformIcons";
import {
  ImageOff,
  Layout,
  PenLine,
  Plus,
  RefreshCw,
  RotateCcw,
  Video,
  X,
  Zap,
} from "lucide-react";
import React from "react";
import { useAds } from "../hooks/AdsContext";
import { extractPostMediaUrl } from "../utils/adsUtils";
import {
  AD_TEXT_LIMITS,
  getRecommendedLimitWarning,
} from "../utils/charLimits";
import CampaignPreview from "./CampaignPreview";

// A thumbnail de vídeo vem de um endpoint do backend (`/api/videos/.../thumbnail`)
// que às vezes retorna 404 (extração de frame falhou/vídeo sem thumbnail
// pronta) — mas a URL do vídeo em si funciona normalmente (o preview grande
// já toca ela). Então, se a imagem da thumbnail falhar, tentamos mostrar o
// primeiro frame do próprio vídeo via <video preload="metadata"> antes de
// cair pro ícone genérico — só o ícone falha se o vídeo também falhar.
const MediaThumb: React.FC<{
  src: string;
  alt: string;
  isVideo?: boolean;
  videoUrl?: string;
  large?: boolean;
}> = ({ src, alt, isVideo, videoUrl, large }) => {
  const [imageError, setImageError] = React.useState(false);
  const [videoError, setVideoError] = React.useState(false);

  const sizeClass = large ? "w-40 h-40" : "w-full h-full";

  if (imageError && isVideo && videoUrl && !videoError) {
    return (
      <video
        src={videoUrl}
        className={cn(sizeClass, large ? "object-contain" : "object-cover")}
        muted
        playsInline
        preload="metadata"
        onError={() => setVideoError(true)}
      />
    );
  }

  if (imageError) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-gray-800",
          sizeClass,
        )}
      >
        {isVideo ? (
          <Video
            className={
              large ? "w-10 h-10 text-white/50" : "w-1/2 h-1/2 text-white/50"
            }
          />
        ) : (
          <ImageOff
            className={
              large ? "w-10 h-10 text-white/50" : "w-1/2 h-1/2 text-white/50"
            }
          />
        )}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={
        large
          ? "block max-w-[240px] max-h-[240px] w-auto h-auto object-contain"
          : "w-full h-full object-cover"
      }
      onError={() => setImageError(true)}
    />
  );
};

// Limites recomendados de cada rede, conforme a documentação oficial de
// cada plataforma de anúncios, antes do texto ser truncado ("Ver mais"):
// - Meta (Facebook/Instagram): Primary Text 125 / Headline 40 / Description 30.
// - LinkedIn (Sponsored Content): Introductory Text 150 / Headline 70.
//   Não há um campo de "Description" separado documentado para esse
//   formato, então esse campo fica sem contador no LinkedIn.
// - TikTok (In-Feed Ads): Ad Text 100. Não há campos de Headline/Description
//   próprios nesse formato, então ficam sem contador.
// - Google Ads (RSA/Display) já tem limites rígidos aplicados via
//   `maxLength` (Headline 30 / Description 90), então não duplicamos com
//   um contador aqui.
// São recomendações de exibição, não bloqueios rígidos da API (texto além
// do limite ainda é aceito por Meta/LinkedIn/TikTok, só é truncado no
// feed) — por isso não usamos `maxLength` para essas três, só sinalizamos
// com cor quando o texto ultrapassa.
const Step2_CreativeEditor: React.FC = () => {
  const {
    platform,
    googleCampaignType,
    message,
    setMessage,
    headline,
    setHeadline,
    description,
    setDescription,
    linkUrl,
    setLinkUrl,
    googlePmaxImageUrl,
    setGooglePmaxImageUrl,
    approvedPosts,
    approvedPostsLoading,
    loadApprovedPosts,
    applyApprovedPostToAd,
    selectedApprovedPost,
    destinationType,
    setApprovedPostId,
    ctaText,
    setCtaText,
    sitelinks,
    setSitelinks,
    updateSitelink,
    suggesting,
    setSuggesting,
    name,
    country,
    youtubeUrls,
    setYoutubeUrls,
    businessName,
    postMediaUrls,
  } = useAds();
  const { t } = useTranslation();

  // Posts aprovados/publicados só são usados aqui (hub de importação).
  // `loadApprovedPosts` já ignora chamadas redundantes para o mesmo
  // contexto (pode já ter sido resolvido pelo prefetch em background do
  // Step 1), então isso não gera request duplicado ao reentrar no step.
  // `useLayoutEffect` para setar `approvedPostsLoading` antes do primeiro
  // paint, evitando qualquer flash de estado intermediário no Select.
  React.useLayoutEffect(() => {
    void loadApprovedPosts(false);
  }, [loadApprovedPosts]);

  const media = selectedApprovedPost
    ? extractPostMediaUrl(selectedApprovedPost)
    : { url: "", type: "image" };

  const imageUrl =
    platform === "google_ads" && googlePmaxImageUrl
      ? googlePmaxImageUrl
      : media.type === "image"
        ? media.url
        : "";

  const videoUrl =
    youtubeUrls[0]?.trim() || (media.type === "video" ? media.url : "");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 animate-in fade-in slide-in-from-bottom-3 duration-400">
      {/* Left Column: Content Editor (7/12) */}
      <div className="lg:col-span-7 space-y-4">
        <div className="bg-white border border-purple-200/70 rounded-2xl p-4 md:p-5 shadow-xs space-y-3.5">
          {/* Compact Header — always horizontal */}
          <div className="flex items-start gap-2 border-b pb-2.5 border-purple-100">
            <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-purple-600 rounded-lg text-white shadow-xs">
              <Layout className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0 h-8 flex flex-col justify-between">
              <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
                {t("Criação do Anúncio")}
              </h2>
              <span className="text-[10px] text-gray-400 font-medium leading-none">
                {t("Copy, títulos, CTA e links de destino")}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => loadApprovedPosts(true)}
              className="shrink-0 bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700 font-bold text-[10px] uppercase tracking-wider min-h-[32px]! h-8 px-2.5 rounded-lg shadow-none"
              disabled={approvedPostsLoading}
              title={t(
                "Busca de novo a lista de posts aprovados/publicados — use se aprovou ou publicou um post agora e ele ainda não apareceu abaixo.",
              )}
              icon={
                <RefreshCw
                  className={`w-3 h-3 ${approvedPostsLoading ? "animate-spin" : ""}`}
                />
              }
            >
              <span className="hidden sm:inline">
                {approvedPostsLoading
                  ? t("Carregando...")
                  : t("Atualizar Lista de Posts")}
              </span>
              <span className="sm:hidden">
                {approvedPostsLoading ? t("Carregando...") : t("Atualizar")}
              </span>
            </Button>
          </div>

          {/* Hub de Posts Aprovados — flat, sem card aninhado */}
          <div className="space-y-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 min-w-0">
                <Select
                  label={t("Importar Post Validado")}
                  value={String(selectedApprovedPost?.id || "")}
                  onChange={(value) => {
                    if (!value) {
                      setApprovedPostId("");
                      return;
                    }
                    const post = approvedPosts.find(
                      (p) => String(p.id) === String(value),
                    );
                    if (post) applyApprovedPostToAd(post);
                  }}
                  options={[
                    {
                      value: "",
                      label: t("Escrita Manual (Do Zero)"),
                      boxIcon: <PenLine />,
                    },
                    ...approvedPosts.map((p) => {
                      const mediaInfo = extractPostMediaUrl(p);
                      return {
                        value: String(p.id),
                        label: `${p.status === "published" ? t("[AO VIVO]") : t("[VALIDADO]")} #${p.id} • ${getPlatformName(p.platform)} • ${(p.content || "").slice(0, 40)}...`,
                        image: mediaInfo.thumbnail,
                        isVideo: mediaInfo.type === "video",
                        noMedia: !mediaInfo.url,
                      };
                    }),
                  ]}
                  size="sm"
                  className="border-purple-200 text-xs font-semibold rounded-xl"
                  isLoading={approvedPostsLoading}
                />
              </div>
              {selectedApprovedPost && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => applyApprovedPostToAd(selectedApprovedPost)}
                  className="w-full sm:w-auto sm:self-end shrink-0 bg-purple-600 hover:bg-purple-700 shadow-xs rounded-xl px-3 h-10 text-xs font-bold whitespace-nowrap"
                  icon={<RotateCcw className="w-3.5 h-3.5" />}
                  title={t(
                    "Reaplica a copy, mídia e link deste post ao anúncio, substituindo o que estiver preenchido nos campos abaixo.",
                  )}
                >
                  {t("Reaplicar do Post")}
                </Button>
              )}
            </div>

            {/* Media Thumbnails — inline, compacto. Feedback imediato do que
                foi importado, por isso vem logo após o Select/Reaplicar,
                antes da Legenda (que só é relevante enquanto se escolhe
                no dropdown, não depois do resultado já estar na tela). */}
            {postMediaUrls.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0">
                  {t("Mídias ({count}):", { count: postMediaUrls.length })}
                </span>
                {postMediaUrls.map((m, i) => (
                  <Tooltip
                    key={i}
                    position="top"
                    delay={150}
                    showArrow={false}
                    className="p-1! min-w-0! max-w-none! bg-transparent! shadow-none!"
                    content={
                      <div className="relative inline-block max-w-[240px] max-h-[240px] rounded-xl overflow-hidden border-2 border-white shadow-2xl bg-gray-900">
                        <MediaThumb
                          src={m.thumbnail || m.url}
                          alt="Preview"
                          isVideo={m.type === "video"}
                          videoUrl={m.url}
                          large
                        />
                        {m.type === "video" && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Video className="w-8 h-8 text-white drop-shadow-md" />
                          </div>
                        )}
                      </div>
                    }
                  >
                    <div
                      className={cn(
                        "relative w-9 h-9 rounded-lg overflow-hidden border shrink-0 transition-all",
                        platform === "google_ads" && "cursor-pointer",
                        platform === "google_ads"
                          ? googlePmaxImageUrl === m.url
                            ? "border-purple-500 ring-2 ring-purple-100 shadow-xs opacity-100"
                            : "border-gray-200 opacity-60 hover:opacity-100"
                          : i === 0
                            ? "border-purple-300 shadow-xs opacity-100"
                            : "border-gray-200 shadow-xs opacity-100",
                      )}
                      onClick={() => {
                        if (platform === "google_ads")
                          setGooglePmaxImageUrl(m.url);
                      }}
                    >
                      <MediaThumb
                        src={m.thumbnail || m.url}
                        alt="Thumbnail"
                        isVideo={m.type === "video"}
                        videoUrl={m.url}
                      />
                      {m.type === "video" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Video className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Legenda de Estados de Mídia — explica os ícones das opções do Select acima */}
            <div className="flex flex-wrap items-center gap-3 py-1.5">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                {t("Legenda:")}
              </span>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-sm bg-purple-100 flex items-center justify-center">
                  <div className="w-2 h-2 bg-purple-400 rounded-xs" />
                </div>
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">
                  {t("Imagem")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-sm bg-gray-900 flex items-center justify-center">
                  <Video className="w-2.5 h-2.5 text-white" />
                </div>
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">
                  {t("Vídeo")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-sm bg-gray-100 flex items-center justify-center">
                  <ImageOff className="w-2.5 h-2.5 text-gray-300" />
                </div>
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">
                  {t("Sem Mídia")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-sm bg-red-50 flex items-center justify-center">
                  <ImageOff className="w-2.5 h-2.5 text-red-300" />
                </div>
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">
                  {t("Mídia Indisponível")}
                </span>
              </div>
            </div>
          </div>

          {/* Divisor sutil separando importação dos campos */}
          <div className="border-t border-purple-100/80" />

          {/* Form Fields in Balanced Grid */}
          <div className="space-y-3.5">
            <Textarea
              label={
                platform === "meta"
                  ? t("Copy Principal (Legenda do Anúncio)")
                  : platform === "linkedin"
                    ? t("Texto Introdutório (Legenda do Post)")
                    : platform === "tiktok"
                      ? t("Texto do Anúncio")
                      : platform === "chatgpt_ads"
                        ? t("Corpo do Anúncio (Chat Card)")
                        : t("Descrição do Anúncio Google")
              }
              labelRight={
                AD_TEXT_LIMITS[platform]?.message ? (
                  <CharCounter
                    current={message.length}
                    recommended={AD_TEXT_LIMITS[platform]!.message!.recommended}
                    hard={AD_TEXT_LIMITS[platform]!.message!.hard}
                  />
                ) : undefined
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("Digite sua oferta ou texto persuasivo aqui...")}
              required={platform === "meta" || platform === "chatgpt_ads"}
              warning={getRecommendedLimitWarning(
                message.length,
                AD_TEXT_LIMITS[platform]?.message,
                t,
              )}
              hint={
                platform === "meta"
                  ? t(
                      "Recomendado até 125 caracteres (a Meta trunca com 'ver mais' no feed) — limite técnico de 2.200.",
                    )
                  : platform === "linkedin"
                    ? t(
                        "Recomendado até 150 caracteres para evitar truncamento — limite técnico de 255.",
                      )
                    : platform === "tiktok"
                      ? t(
                          "Recomendado até 60 caracteres — limite técnico de 100.",
                        )
                      : platform === "chatgpt_ads"
                        ? t(
                            "Texto curto exibido no card dentro da conversa — sempre obrigatório.",
                          )
                        : t(
                            "Obrigatório se o Título de Destaque estiver vazio.",
                          )
              }
              autoResize={true}
              inputSize="sm"
              className="rounded-xl border-purple-200 focus:border-purple-500 text-xs sm:text-sm min-h-[76px]"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <Input
                label={t("Título de Destaque (Headline)")}
                labelRight={
                  AD_TEXT_LIMITS[platform]?.headline ? (
                    <CharCounter
                      current={headline.length}
                      recommended={
                        AD_TEXT_LIMITS[platform]!.headline!.recommended
                      }
                      hard={AD_TEXT_LIMITS[platform]!.headline!.hard}
                    />
                  ) : undefined
                }
                hint={
                  platform === "google_ads"
                    ? t(
                        "Máx. 30 caracteres. Obrigatório se a Copy estiver vazia.",
                      )
                    : platform === "meta"
                      ? t(
                          "Recomendado até 40 caracteres — limite técnico de 255.",
                        )
                      : platform === "linkedin"
                        ? t("Máx. 70 caracteres.")
                        : platform === "chatgpt_ads"
                          ? t(
                              "Título curto do card. Se vazio, usa o Nome da Campanha.",
                            )
                          : t("Obrigatório se a Copy estiver vazia.")
                }
                warning={getRecommendedLimitWarning(
                  headline.length,
                  AD_TEXT_LIMITS[platform]?.headline,
                  t,
                )}
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder={t("Ex: Oferta Exclusiva 50% OFF")}
                maxLength={
                  platform === "google_ads"
                    ? 30
                    : platform === "linkedin"
                      ? 70
                      : undefined
                }
                inputSize="sm"
                className="text-xs font-semibold rounded-xl border-purple-200"
              />

              {platform === "chatgpt_ads" ? (
                <div className="flex flex-col justify-end">
                  <span className="text-[10px] font-bold text-gray-700 uppercase tracking-tight mb-1">
                    {t("Chamada para Ação (CTA)")}
                  </span>
                  <div className="h-10 px-3.5 rounded-xl border border-emerald-200 bg-emerald-50/40 flex items-center text-[11px] text-emerald-700 font-medium">
                    {t(
                      "Não aplicável — o card do ChatGPT usa um botão de link fixo para a URL de destino.",
                    )}
                  </div>
                </div>
              ) : (
                <Select
                  label={t("Chamada para Ação (CTA)")}
                  hint={t(
                    "Escolha um botão coerente com o Destino selecionado (ex: WhatsApp/Direct/Messenger só fazem sentido se o destino for de mensagens).",
                  )}
                  value={ctaText}
                  onChange={(v) => setCtaText(v as string)}
                  options={[
                    { value: "LEARN_MORE", label: t("Saiba mais") },
                    {
                      value: "WHATSAPP_MESSAGE",
                      label: t("Enviar mensagem (WhatsApp)"),
                    },
                    {
                      value: "SEND_MESSAGE",
                      label: t("Enviar mensagem (Direct)"),
                    },
                    {
                      value: "MESSAGE_PAGE",
                      label: t("Enviar mensagem (Messenger)"),
                    },
                    { value: "SIGN_UP", label: t("Cadastrar-se") },
                    { value: "DOWNLOAD", label: t("Baixar") },
                    { value: "SHOP_NOW", label: t("Comprar agora") },
                    { value: "GET_OFFER", label: t("Obter oferta") },
                    { value: "BOOK_NOW", label: t("Agendar agora") },
                    { value: "CONTACT_US", label: t("Fale conosco") },
                    { value: "APPLY_NOW", label: t("Inscrever-se") },
                    { value: "CALL_NOW", label: t("Ligar agora") },
                    { value: "SUBSCRIBE", label: t("Assinar") },
                  ]}
                  size="sm"
                  className="text-xs rounded-xl border-purple-200 font-semibold"
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <Input
                label={t("URL / Link de Destino")}
                hint={
                  destinationType === "website"
                    ? undefined
                    : t(
                        "Só é obrigatório quando o Destino é 'Site' — pode deixar vazio para os demais destinos.",
                      )
                }
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://..."
                required={destinationType === "website"}
                inputSize="sm"
                className="text-xs rounded-xl border-purple-200"
              />

              <Input
                label={t("Subtítulo Descritivo")}
                labelRight={
                  AD_TEXT_LIMITS[platform]?.description ? (
                    <CharCounter
                      current={description.length}
                      recommended={
                        AD_TEXT_LIMITS[platform]!.description!.recommended
                      }
                      hard={AD_TEXT_LIMITS[platform]!.description!.hard}
                    />
                  ) : undefined
                }
                hint={
                  platform === "google_ads"
                    ? t("Máx. 90 caracteres")
                    : platform === "meta"
                      ? t(
                          "Recomendado até 30 caracteres (raramente é exibido no feed).",
                        )
                      : platform === "linkedin"
                        ? t(
                            "Máx. 70 caracteres (usado só no LinkedIn Audience Network).",
                          )
                        : undefined
                }
                warning={getRecommendedLimitWarning(
                  description.length,
                  AD_TEXT_LIMITS[platform]?.description,
                  t,
                )}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("Contexto complementar para o lead...")}
                maxLength={
                  platform === "google_ads"
                    ? 90
                    : platform === "linkedin"
                      ? 70
                      : undefined
                }
                inputSize="sm"
                className="text-xs rounded-xl border-purple-200"
              />
            </div>

            {/* Google Ads Specific Settings */}
            {platform === "google_ads" && (
              <div className="p-3.5 bg-blue-50/20 rounded-xl border border-blue-200/70 space-y-3 animate-in fade-in duration-300">
                <Input
                  label={t("Link da Imagem (PMax/Display)")}
                  hint={t(
                    "Usa o mesmo valor das miniaturas de mídia acima — clicar numa miniatura substitui o que estiver aqui, e vice-versa.",
                  )}
                  value={googlePmaxImageUrl}
                  onChange={(e) => setGooglePmaxImageUrl(e.target.value)}
                  placeholder="https://url-imagem.com/thumb.jpg"
                  inputSize="sm"
                  className="text-xs rounded-xl border-blue-200"
                />

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
                      {t("Vídeos do YouTube (Opcional)")}
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setYoutubeUrls([...youtubeUrls, ""])}
                      className="text-[10px] font-bold text-blue-600 uppercase hover:bg-blue-50 hover:text-blue-700 h-7 px-2 min-h-0 rounded-lg shadow-none"
                      icon={<Plus className="w-3 h-3" />}
                    >
                      {t("Adicionar Vídeo")}
                    </Button>
                  </div>
                  {youtubeUrls.map((url, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="flex-1">
                        <Input
                          value={url}
                          onChange={(e) => {
                            const newUrls = [...youtubeUrls];
                            newUrls[idx] = e.target.value;
                            setYoutubeUrls(newUrls);
                          }}
                          placeholder="https://youtube.com/watch?v=..."
                          inputSize="sm"
                          className="text-xs rounded-xl border-blue-200"
                        />
                      </div>
                      {youtubeUrls.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setYoutubeUrls(
                              youtubeUrls.filter((_, i) => i !== idx),
                            )
                          }
                          className="w-10 h-10 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors shrink-0"
                          title={t("Remover Vídeo")}
                          aria-label={t("Remover Vídeo")}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Sitelinks Section */}
                <div className="pt-3 border-t border-blue-100 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-gray-700 uppercase tracking-tight">
                      {t("Sitelinks Google")}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (!linkUrl) {
                          showToastWarning(
                            t("Insira uma URL de destino primeiro."),
                          );
                          return;
                        }
                        setSuggesting(true);
                        try {
                          const res = await apiService.post(
                            "/api/ads/google/research",
                            {
                              business_description:
                                description || message || name,
                              website_url: linkUrl,
                              target_location: country || "Brasil",
                            },
                          );
                          if (res.success && res.data?.sitelinks) {
                            const suggested = res.data.sitelinks.map(
                              (s: any) => ({
                                title: s.title || s.text || "",
                                description1: s.desc1 || s.description1 || "",
                                description2: s.desc2 || s.description2 || "",
                                finalUrl: linkUrl,
                              }),
                            );
                            const newSitelinks = [...sitelinks];
                            suggested.forEach((s: any, i: number) => {
                              if (i < 6) newSitelinks[i] = s;
                            });
                            setSitelinks(newSitelinks);
                            showToastSuccess(
                              t("Sitelinks sugeridos com sucesso!"),
                            );
                          }
                        } catch {
                          showToastError(t("Erro ao obter sugestões."));
                        } finally {
                          setSuggesting(false);
                        }
                      }}
                      disabled={suggesting}
                      className="min-h-[32px]! h-8 px-3 text-[10px] font-bold uppercase border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 hover:border-blue-300 rounded-lg shadow-none"
                      icon={
                        <Zap
                          className={`w-3 h-3 ${suggesting ? "animate-pulse" : ""}`}
                        />
                      }
                    >
                      {suggesting ? t("Sugerindo...") : t("Sugerir com IA")}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                    {sitelinks.map((sitelink, index) => (
                      <div
                        key={index}
                        className="p-2.5 bg-white/70 rounded-xl space-y-2 text-xs"
                      >
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          {t("Sitelink #{index}", { index: index + 1 })}
                        </div>
                        <Input
                          label={t("Título")}
                          hint={t("Máx. 25 caracteres")}
                          placeholder={t("Ex: Fale Conosco")}
                          value={sitelink.title}
                          onChange={(e) =>
                            updateSitelink(index, { title: e.target.value })
                          }
                          maxLength={25}
                          inputSize="sm"
                          className="text-xs rounded-xl"
                        />
                        <Input
                          label={t("Descrição 1")}
                          hint={t("Máx. 35 caracteres")}
                          placeholder={t(
                            "Ex: Nossa equipe aguarda seu contato",
                          )}
                          value={sitelink.description1}
                          onChange={(e) =>
                            updateSitelink(index, {
                              description1: e.target.value,
                            })
                          }
                          maxLength={35}
                          inputSize="sm"
                          className="text-[11px] rounded-xl"
                        />
                        <Input
                          label={t("Descrição 2")}
                          hint={t("Máx. 35 caracteres")}
                          placeholder={t("Ex: Atendimento rápido e sem custo")}
                          value={sitelink.description2}
                          onChange={(e) =>
                            updateSitelink(index, {
                              description2: e.target.value,
                            })
                          }
                          maxLength={35}
                          inputSize="sm"
                          className="text-[11px] rounded-xl"
                        />
                        <Input
                          label={t("URL")}
                          placeholder={t("https://...")}
                          value={sitelink.finalUrl}
                          onChange={(e) =>
                            updateSitelink(index, { finalUrl: e.target.value })
                          }
                          inputSize="sm"
                          className="text-[11px] rounded-xl"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Column: Sticky Live Preview (5/12) */}
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-4 space-y-4">
          <div className="bg-white rounded-2xl border border-purple-100 shadow-md overflow-hidden">
            <CampaignPreview
              platform={platform}
              googleCampaignType={googleCampaignType}
              headline={headline}
              message={message}
              description={description}
              linkUrl={linkUrl}
              imageUrl={imageUrl}
              videoUrl={videoUrl}
              ctaText={ctaText}
              businessName={businessName}
              sitelinks={sitelinks}
              postMediaUrls={postMediaUrls}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2_CreativeEditor;
